import { Skeleton } from '@heroui/react';
import { useState } from 'react';
import { cn } from '@/shared/lib/cn';
import { isTutorImageReady, markTutorImageReady } from '../model/tutorImages';
import type { Tutor } from '../model/tutors';

/**
 * Фон листов персонажей (`references/mascot/`) — под картинкой вместо цветного
 * `backdrop`: тот рассчитан на эмодзи и, пока картинка грузится, мелькал
 * коричневым и фиолетовым.
 */
const SHEET_BACKGROUND = '#eaebee';

interface TutorAvatarProps {
  tutor: Tutor;
  /**
   * Какую картинку показать: `portrait` — голова и плечи для кружков,
   * `preview` — в рост для карточки выбора. Не задано или у тьютора нет
   * картинок — эмодзи-заглушка.
   */
  image?: 'portrait' | 'preview';
  className?: string;
  /** Размер эмодзи-заглушки: карточка в сетке и портрет на весь экран разные. */
  emojiClassName?: string;
}

/**
 * Портрет тьютора. Картинка и заглушка держат одну геометрию (её задаёт
 * `className` вызывающей стороны), поэтому раскладка от них не зависит.
 */
export function TutorAvatar({ tutor, image, className, emojiClassName }: TutorAvatarProps) {
  const src = image && tutor.images?.[image];
  const [loadedSrc, setLoadedSrc] = useState<string | null>(null);
  const isLoaded = !!src && (loadedSrc === src || isTutorImageReady(src));
  const [fadeIn] = useState(!isLoaded);
  const onReady = () => {
    if (!src) return;
    markTutorImageReady(src);
    setLoadedSrc(src);
  };

  if (!src) {
    return (
      <div
        aria-hidden="true"
        style={{ background: tutor.backdrop }}
        className={cn('flex items-center justify-center overflow-hidden select-none', className)}
      >
        <span className={cn('text-5xl leading-none', emojiClassName)}>{tutor.avatar}</span>
      </div>
    );
  }

  return (
    <div
      aria-hidden="true"
      style={{ background: SHEET_BACKGROUND }}
      className={cn('relative overflow-hidden select-none', className)}
    >
      {!isLoaded && <Skeleton className="absolute inset-0 rounded-none" />}
      <img
        // Из кэша картинка может оказаться готовой раньше, чем React повесит onLoad.
        ref={(node) => {
          if (!isLoaded && node?.complete && node.naturalWidth > 0) onReady();
        }}
        src={src}
        alt=""
        decoding="async"
        draggable={false}
        onLoad={isLoaded ? undefined : onReady}
        // Проявление — только для картинки, которую действительно ждали:
        // готовая к монтированию встаёт сразу, без анимации.
        className={cn(
          'relative size-full object-cover',
          fadeIn && 'transition-opacity duration-300',
          isLoaded ? 'opacity-100' : 'opacity-0',
        )}
      />
    </div>
  );
}
